import { Tab, Tabs, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import {InfoOutlined} from '@mui/icons-material';
import BarChart from '@components/Charts/BarCharts';
import { useFilter } from '../contexts/FilterContext';
import { CircularProgress } from '@mui/material';

const TopSectors: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const { timeRange, selectedTab } = useFilter();

  const [sectors, setSectors] = useState<any[]>([]); // If you have a type, replace `any` with the sector type

useEffect(() => {
  const loadData = async () => {
    try {
      const sectorModule = await import(`../json/${selectedTab}/sectors.json`);
      setSectors(sectorModule.default || []);
    } catch (error) {
      console.error(`Error loading sectors for tab "${selectedTab}"`, error);
      setSectors([]);
    }
  };

  loadData();
}, [selectedTab]);

if (sectors.length === 0) {
  return (
    <div className="flex-grow-1 border rounded p-3 bg-white d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
      <CircularProgress size={24} style={{ color: '#043C5B' }} />
    </div>
  );
}

  const employmentData = sectors.map((sector) => ({
    name: sector.value,
    value: sector.total_employed || 0,
  }))
  .sort((a, b) => b.value - a.value)
  .slice(0, 6);


const growthDataRaw = sectors
  .filter(s => s.value !== 'All')
  .map(sector => ({
    name: sector.value,
    value: sector.average_growth_rate[timeRange as keyof typeof sector.average_growth_rate] || 0,
  }));


const topPositiveGrowth = growthDataRaw
  .filter(s => s.value > 0)
  .sort((a, b) => b.value - a.value)
  .slice(0, 3);

const bottomNegativeGrowth = growthDataRaw
  .filter(s => s.value < 0)
  .sort((a, b) => a.value - b.value)
  .slice(0, 3);

const growthData = [...topPositiveGrowth, ...bottomNegativeGrowth];
  const maxGrowthValue = Math.max(...growthData.map(d => Math.abs(d.value)));


  return (
    <div className="flex-grow-1 border rounded p-3 bg-white" style={{ minWidth: '300px', flex: 1 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pb-4">
        <div>
          <h6 className="mb-1 d-flex align-items-center gap-1" style={{ fontSize: 20 }}>
            Top Sectors
            <Tooltip title="Select a sector to filter the dashboard." arrow>
              <InfoOutlined sx={{ fontSize: 16, color: '#6b7280', cursor: 'pointer' }} />
            </Tooltip>
          </h6>
          <p className="text-muted small mb-0">Select a sector to filter the dashboard.</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(e, newValue) => setTabIndex(newValue)}
        variant="fullWidth"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          borderBottom: '1px solid #D7DAE0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: 14,
            color: '#374151',
            borderBottom: '1px solid #D7DAE0',
            borderRadius: 0,
            minWidth: 0,
            '&:hover': {
              color: '#043C5B',
              fontWeight: 600,
              borderBottom: '1px solid #043C5B',
              background: '#F3F5F7',
            },
          },
          '& .Mui-selected': {
            color: '#043C5B',
            fontWeight: 600,
            borderBottom: '1px solid #043C5B',
            background: '#F3F5F7',
          },
        }}
      >
        <Tab label="Employment" />
        <Tab label="Growth/Decline" />
      </Tabs>

      {/* Tab Content */}
      <div className="pt-3">
        {tabIndex === 0 && (
          <div>
      <BarChart
        name="sector-bar"
        data={employmentData.filter(s => s.name !== 'All')}
        chartHeight={600}
      />
          </div>
        )}
{tabIndex === 1 && (
  <div>
    <table className="table table-sm mb-0">
      <tbody>
        {growthData
          .filter(s => s.name !== 'All')
          .sort((a, b) => b.value - a.value)
          .map((item, idx) => {
            const isPositive = item.value >= 0;
            const barWidth = Math.min((Math.abs(item.value) / maxGrowthValue) * 100, 100);
            const formatted = `${isPositive ? '+' : ''}${item.value.toFixed(2)}%`;

            return (
              <tr key={idx} className="border-0">
                <td className="border-0" style={{ width: '30%', padding: '8px 12px', background: "#ffffff" }}>
                  {!isPositive && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title={formatted} arrow>
                        <div
                          style={{
                            width: `${barWidth}%`,
                            height: 12,
                            backgroundColor: '#931035',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </td>

                {/* Name (middle) */}
                <td className="border-0" style={{ width: '40%', textAlign: 'center', fontSize: 14, color: '#374151', background: "#ffffff" }}>
                  {item.name}
                </td>

                {/* Positive (right) */}
                <td className="border-0 py-4" style={{ width: '30%', padding: '8px 12px', background: "#ffffff" }}>
                  {isPositive && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Tooltip title={formatted} arrow>
                        <div
                          style={{
                            width: `${barWidth}%`,
                            height: 12,
                            backgroundColor: '#2F7C0D',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  </div>
)}


      </div>
    </div>
  );
};

export default TopSectors;
